/**
 * Resources API
 *
 * The org-wide Resources page: sections of links, files and notes that every
 * member can read and only owners/admins can change.
 */

import { api } from './client';

// =============================================================================
// Types
// =============================================================================

export interface ResourceFile {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

/** A link (url) or an uploaded file (file), never both. */
export interface ResourceItem {
  id: string;
  title: string;
  url: string | null;
  file: ResourceFile | null;
  notes: string | null;
}

export interface ResourceSection {
  id: string;
  title: string;
  notes: string | null;
  items: ResourceItem[];
}

export interface ResourceSectionInput {
  title: string;
  notes: string | null;
  items: Array<{ title: string; url?: string; fileId?: string; notes: string | null }>;
}

export interface ListResourcesResponse {
  sections: ResourceSection[];
  /** Whether this org's plan allows uploads. Links are free on every plan. */
  canUploadFiles: boolean;
}

// =============================================================================
// Requests
// =============================================================================

export async function listResources(): Promise<ListResourcesResponse> {
  const response = await api.get<ListResourcesResponse>('/resources');
  return response.data;
}

export async function createResourceSection(data: ResourceSectionInput): Promise<ResourceSection> {
  const response = await api.post<{ section: ResourceSection }>('/resources', data);
  return response.data.section;
}

export async function updateResourceSection(
  id: string,
  data: ResourceSectionInput
): Promise<ResourceSection> {
  const response = await api.put<{ section: ResourceSection }>(`/resources/${id}`, data);
  return response.data.section;
}

export async function deleteResourceSection(id: string): Promise<void> {
  await api.delete(`/resources/${id}`);
}

export async function reorderResourceSections(sectionIds: string[]): Promise<void> {
  await api.put('/resources/reorder', { sectionIds });
}

/** Store a file for a section save to attach. Paid plans only. */
export async function uploadResourceFile(file: File): Promise<ResourceFile> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<{ file: ResourceFile }>('/resources/files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.file;
}

export async function downloadResourceFile(id: string): Promise<Blob> {
  const response = await api.get<Blob>(`/resources/files/${id}/download`, { responseType: 'blob' });
  return response.data;
}
